import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { AutoCompleteCompleteEvent } from 'primeng/autocomplete';
import { ApsPermitedScreen } from '../../../models/aps-permission/aps-permited-screen';
import { ConstantsFordProtect } from '../../../models/constants/constants-ford-protect';
import { Patterns } from '../../../models/constants/patterns';
import { OperationCode } from '../../../models/enum/operation-code';
import { StatusProduct } from '../../../models/enum/status-product';
import { CoverageCategory } from '../../../models/product/coverage-category';
import { NewCoverageCategory } from '../../../models/product/new-coverage-category';
import { ProductActive } from '../../../models/product/product-active';
import { ApsPermissionService } from '../../../services/aps-permission/aps-permission.service';
import { CoverageService } from '../../../services/coverage/coverage.service';
import { ProductService } from '../../../services/product/product.service';

type ValueChangeEvent<T> = { value: T };
type EditableOption = { label: string; value: string | null };

@Component({
  selector: 'app-new-coverage',
  templateUrl: './new-coverage.component.html',
  styleUrls: ['./new-coverage.component.scss'],
  standalone: false
})
export class NewCoverageComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly focusOrder: string[] = [
    'optionCountry',
    'optionProductName',
    'optionCoverageType',
    'inputCoverageName',
    'inputCoverageDescription',
    'inputOwsCoverage',
    'inputMonths',
    'inputDistance',
    'inputTotalVisits',
    'inputInitialVisit',
    'optionAccount',
    'optionActionCode',
    'optionCoverageCode',
    'optionInsuranceCode'
  ];

  listCountries = [
    { label: 'TRANSLATE.CHOOSE.COUNTRY.ARG', value: ConstantsFordProtect.ARG },
    { label: 'TRANSLATE.CHOOSE.COUNTRY.BRA', value: ConstantsFordProtect.BRA },
    { label: 'TRANSLATE.CHOOSE.COUNTRY.CHL', value: ConstantsFordProtect.CHL },
    { label: 'TRANSLATE.CHOOSE.COUNTRY.COL', value: ConstantsFordProtect.COL },
    { label: 'TRANSLATE.CHOOSE.COUNTRY.VEN', value: ConstantsFordProtect.VEN },
  ];

  listTypeCoverage = [
    { label: 'TRANSLATE.UPPERCASE.MAINTENANCE', value: ConstantsFordProtect.MAINTENANCE },
    { label: 'TRANSLATE.UPPERCASE.EXTENSION', value: ConstantsFordProtect.EXTENSION },
    { label: 'TRANSLATE.UPPERCASE.REPAIRS', value: ConstantsFordProtect.REPAIRS },
    { label: 'TRANSLATE.UPPERCASE.SIGNALS', value: ConstantsFordProtect.SIGNALS },
    { label: 'TRANSLATE.UPPERCASE.WINCHES', value: ConstantsFordProtect.WINCHES },
    { label: 'TRANSLATE.UPPERCASE.WEAR', value: ConstantsFordProtect.WEAR }
  ];

  userform!: UntypedFormGroup;
  coverageCategory: CoverageCategory;
  newCoverageCategory: NewCoverageCategory;
  selectedCountry: string | null = null;
  selectedProductName: number | null = null;
  selectedCoverageType: string | null = null;
  placeholderProduct = 'TRANSLATE.SELECT';
  isLoadingProducts = false;

  productActiveCountries: ProductActive[] = [];
  allProductActiveCountries: ProductActive[] = [];  // Backup of original data for filtering
  productActive!: ProductActive;
  productPlanYear: string = '';
  productAccount: string = '';
  productActionCode: string = '';
  productCoverageCode: string = '';
  productInsuranceCode: string = '';
  blockedUi = false;
  isSaving = false;
  isCtaSuccess = false;
  submitted = false;
  private debounceTimer: ReturnType<typeof setTimeout> | undefined;


  coverageCategorys: CoverageCategory[] = [];

  accountCodeUppercase: string = '';
  editableAccount = false;
  messageAccountCode = false;
  placeholderAccountCode: string = '';

  actionCodeUppercase: string = '';
  editableActionCodeOWS = false;
  messageActionCodeOWS = false;
  placeholderActionCodeOWS: string = '';

  coverageUppercase: string = '';
  editableCoverageCodeOWS = false;
  messageCoverageCodeOWS = false;
  placeholderCoverageCodeOWS: string = '';

  insuranceCodeUppercase: string = '';
  editableInsuranceCode = false;
  messageInsuranceCode = false;
  placeholderInsuranceCode: string = '';
  inputOdometer: number = 0;

  operation = OperationCode.CAR;
  statusProduct = StatusProduct.ACTIVE;
  private readonly nullOptionValue = '__NULL_OPTION__';

  listAccountCodeOWS = [
    { label: ConstantsFordProtect.ESB, value: ConstantsFordProtect.ESB },
    { label: ConstantsFordProtect.ESBG, value: ConstantsFordProtect.ESBG },
    { label: ConstantsFordProtect.ESMP, value: ConstantsFordProtect.ESMP },
    { label: ConstantsFordProtect.EXTE, value: ConstantsFordProtect.EXTE },
    { label: ConstantsFordProtect.FLEE, value: ConstantsFordProtect.FLEE },
    { label: ConstantsFordProtect.FLSN, value: ConstantsFordProtect.FLSN },
    { label: ConstantsFordProtect.FLMT, value: ConstantsFordProtect.FLMT },
    { label: ConstantsFordProtect.NULO, value: this.nullOptionValue },
    { label: ConstantsFordProtect.OTHERS, value: ConstantsFordProtect.OTHERS }
  ];

  listActionCodeOWS = [
    { label: ConstantsFordProtect.A, value: ConstantsFordProtect.A },
    { label: ConstantsFordProtect.NULO, value: this.nullOptionValue },
    { label: ConstantsFordProtect.OTHERS, value: ConstantsFordProtect.OTHERS }
  ];

  listCoverageCodeOWS = [
    { label: ConstantsFordProtect.AST, value: ConstantsFordProtect.AST },
    { label: ConstantsFordProtect.ESP, value: ConstantsFordProtect.ESP },
    { label: ConstantsFordProtect.FLMT, value: ConstantsFordProtect.FLMT },
    { label: ConstantsFordProtect.FLSN, value: ConstantsFordProtect.FLSN },
    { label: ConstantsFordProtect.QCM, value: ConstantsFordProtect.QCM },
    { label: ConstantsFordProtect.NULO, value: this.nullOptionValue },
    { label: ConstantsFordProtect.OTHERS, value: ConstantsFordProtect.OTHERS }
  ];

  listInsuranceCode = [
    { label: ConstantsFordProtect.AST, value: ConstantsFordProtect.AST },
    { label: ConstantsFordProtect.MNTC, value: ConstantsFordProtect.MNTC },
    { label: ConstantsFordProtect.QCM, value: ConstantsFordProtect.QCM },
    { label: ConstantsFordProtect.REP, value: ConstantsFordProtect.REP },
    { label: ConstantsFordProtect.WTY, value: ConstantsFordProtect.WTY },
    { label: ConstantsFordProtect.NULO, value: this.nullOptionValue },
    { label: ConstantsFordProtect.OTHERS, value: ConstantsFordProtect.OTHERS }
  ];

  constructor(
    private fb: UntypedFormBuilder,
    private serviceProduct: ProductService,
    private messageService: MessageService,
    private apsPermissionService: ApsPermissionService,
    private translateService: TranslateService,
    private router: Router,
    private coverageService: CoverageService,
    private productService: ProductService
  ) {
    this.productActive = new ProductActive();
    this.coverageCategory = new CoverageCategory();
    this.newCoverageCategory = new NewCoverageCategory();
  }



  ngOnInit() {

    this.apsPermissionService.permissionValidation(ApsPermitedScreen.screenViewsPlans);

    this.userform = this.fb.group({
      'optionCountry': [null, Validators.required],
      'optionProductName': [{ value: null, disabled: true }, Validators.required],
      'optionCoverageType': [null, Validators.required],
      'inputCoverageName': ['', [Validators.required, Validators.minLength(4), Validators.maxLength(20)]],
      'inputCoverageDescription': ['', [Validators.required, Validators.minLength(4), Validators.maxLength(100)]],
      'inputOwsCoverage': ['', [Validators.required, Validators.minLength(4), Validators.maxLength(8)]],
      'inputMonths': ['', [Validators.required, Validators.min(1), Validators.max(99), Validators.maxLength(2), Validators.pattern(Patterns.ONLY_NUMBERS)]],
      'inputDistance': ['', [Validators.required, Validators.min(1), Validators.max(999999), Validators.maxLength(7), Validators.pattern(Patterns.NUMBERS_POINT)]],
      'inputTotalVisits': ['', [Validators.required, Validators.min(1), Validators.max(99), Validators.maxLength(2), Validators.pattern(Patterns.ONLY_NUMBERS)]],
      'optionAccount': [{ value: undefined, disabled: true }, [this.optionalCodeMaxLengthValidator(9, () => this.editableAccount, () => this.listAccountCodeOWS)]],
      'optionActionCode': [{ value: undefined, disabled: true }, [this.optionalCodeMaxLengthValidator(1, () => this.editableActionCodeOWS, () => this.listActionCodeOWS)]],
      'optionCoverageCode': [{ value: undefined, disabled: true }, [this.optionalCodeMaxLengthValidator(4, () => this.editableCoverageCodeOWS, () => this.listCoverageCodeOWS)]],
      'optionInsuranceCode': [{ value: undefined, disabled: true }, [this.insuranceCodeRequiredValidator(4, () => this.editableInsuranceCode, () => this.listInsuranceCode)]],
      'inputInitialVisit': ['', [Validators.required, Validators.min(1), Validators.max(99), Validators.maxLength(2), Validators.pattern(Patterns.ONLY_NUMBERS)]]
    });

    this.placeholderAccountCode = 'TRANSLATE.SELECT';
    this.placeholderActionCodeOWS = 'TRANSLATE.SELECT';
    this.placeholderCoverageCodeOWS = 'TRANSLATE.SELECT';
    this.placeholderInsuranceCode = 'TRANSLATE.SELECT';

    // Smart debounce with status tracking
    // Listen to form validity changes separately from value changes
    this.userform.statusChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((status) => {
        // Immediately disable button if form becomes invalid
        if (status !== 'VALID') {
          clearTimeout(this.debounceTimer);
          this.isCtaSuccess = false;
        }
      });

    // Listen to value changes and debounce the enable
    this.userform.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        // If form is not valid, skip debounce
        if (this.userform?.status !== 'VALID') {
          return;
        }

        // Form is valid - clear old timer and start new debounce
        clearTimeout(this.debounceTimer);
        this.debounceTimer = setTimeout(() => {
          // Only enable if form is still valid after debounce
          if (this.userform?.status === 'VALID') {
            this.isCtaSuccess = true;
          }
        }, 1500);
      });

  }

  private checkFormCompletion(): void {
    this.isCtaSuccess = this.userform?.valid ?? false;
  }

  private getInputTarget(event: Event): HTMLInputElement | null {
    return event.target instanceof HTMLInputElement ? event.target : null;
  }

  buttonReturn() {
    this.router.navigate(['view-plans'], { skipLocationChange: false, replaceUrl: false });
  }

  countryChange(event: ValueChangeEvent<string>) {
    this.selectedCountry = event.value;

    this.resetProductSelectionState();

    if (!this.selectedCountry) {
      this.placeholderProduct = 'TRANSLATE.SELECT.COUNTRY.FIRST';
      this.isLoadingProducts = false;
      this.userform.get('optionProductName')?.disable({ emitEvent: false });
      return;
    }

    this.placeholderProduct = 'TRANSLATE.LOADING.PRODUCTS';
    this.isLoadingProducts = true;
    this.userform.get('optionProductName')?.disable({ emitEvent: false });

    this.searchProductActiveCountry(this.selectedCountry);
  }

  private searchProductActiveCountry(country: string) {

    this.serviceProduct.getActivesProductsByCountry(country, this.operation, this.statusProduct as number)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: ProductActive[]) => {

          this.productActiveCountries = response;
          this.allProductActiveCountries = [...response];  // Backup original data for filtering
          this.isLoadingProducts = false;

          if (this.productActiveCountries.length > 0) {
            this.productActive = new ProductActive();
            this.selectedProductName = null;
            this.userform.get('optionProductName')?.enable({ emitEvent: false });
            this.userform.get('optionProductName')?.setValue(null, { emitEvent: false });
            this.placeholderProduct = 'TRANSLATE.SELECT';

          } else {
            this.productActive = new ProductActive();
            this.placeholderProduct = 'TRANSLATE.NO.RESULT.FOUND';
            this.userform.get('optionProductName')?.disable({ emitEvent: false });
          }

        }, error: (err) => {
          this.isLoadingProducts = false;
          this.placeholderProduct = 'TRANSLATE.NO.RESULT.FOUND';
          this.userform.get('optionProductName')?.disable({ emitEvent: false });

          if (err.status === 402) {
            this.messageService.add({ severity: 'warn', summary: 'Atenção', detail: 'Pagamento recusado. Verifique os dados do cartão e tente novamente.' })
            return;
          }
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('TRANSLATE.ERROR'),
            detail: this.translateService.instant('TRANSLATE.PRODUCTS.NOT.FOUND')
          });
        }
      });
  }

  formatNumber(event: Event) {

    const input = this.getInputTarget(event);
    if (!input) {
      return '';
    }

    const value = input.value;
    const valueFormat = value.replace(/[^0-9]/g, '');

    input.value = valueFormat;
    return valueFormat;
  }

  formatOdometerNumber(event: Event) {

    const input = this.getInputTarget(event);
    if (!input) {
      this.inputOdometer = 0;
      return;
    }

    let odometereTreatment = input.value;

    odometereTreatment = odometereTreatment.replace(/\D/g, "");
    odometereTreatment = odometereTreatment.replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1.");

    input.value = odometereTreatment;

    const valueWithoutPoint = odometereTreatment.replace(/\./g, '').replace('.', ',');
    this.inputOdometer = parseInt(valueWithoutPoint);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  productChange(event: any) {
    // IMPORTANT: Reset state BEFORE loading new product data
    this.resetCodeFieldsState();

    // Handle p-autoComplete onSelect event with single ProductActive selection
    const selectedProduct = event?.value instanceof ProductActive
      ? event.value
      : event instanceof ProductActive
        ? event
        : null;

    this.selectedProductName = selectedProduct?.id ?? null;
    this.productActive = selectedProduct ?? new ProductActive();

    // Sync with form control
    if (selectedProduct) {
      this.userform.get('optionProductName')?.setValue(selectedProduct, { emitEvent: false });
      this.getProductById(selectedProduct.id);
    } else {
      this.userform.get('optionProductName')?.setValue(null, { emitEvent: false });
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onProductSelect(event: any) {
    // Called when user selects an item from p-autoComplete suggestions
    const selectedProduct = event?.value;

    // IMPORTANT: Reset state BEFORE loading new product data
    this.resetCodeFieldsState();

    this.selectedProductName = selectedProduct?.id ?? null;
    this.productActive = selectedProduct ?? new ProductActive();

    // Sync with form control
    if (selectedProduct) {
      this.userform.get('optionProductName')?.setValue(selectedProduct, { emitEvent: false });
      this.getProductById(selectedProduct.id);
    } else {
      this.userform.get('optionProductName')?.setValue(null, { emitEvent: false });
    }
  }

  formatProductIdName(item: ProductActive | string | null | undefined): string {
    // Safety check: return empty string for invalid inputs
    if (!item) {
      return '';
    }

    // If it's a string, return as-is
    if (typeof item === 'string') {
      return item;
    }

    // If it's not an object, return empty
    if (typeof item !== 'object') {
      return '';
    }

    // Extract id and name safely
    const idValue = item.id;
    const nameValue = item.name;

    const idText = idValue ? String(idValue).trim() : '';
    const nameText = nameValue ? String(nameValue).trim() : '';

    // Return combinations - avoid duplicates by checking for separator
    if (idText && nameText) {
      // Check if name already contains the id to avoid duplication
      if (nameText.includes(idText)) {
        return nameText;
      }
      return `${idText} - ${nameText}`;
    } else if (idText) {
      return idText;
    } else if (nameText) {
      return nameText;
    }

    return '';
  }

  searchProducts(event: AutoCompleteCompleteEvent) {
    // PrimeNG AutoComplete completeMethod handler
    // Filters products based on user input and assigns back to [suggestions]
    // Called when: user types OR user clicks dropdown button

    const query = event.query || '';
    const queryLower = query.toLowerCase().trim();

    if (!queryLower) {
      // No filter - show all products from backup array
      this.productActiveCountries = [...this.allProductActiveCountries];
    } else {
      // Filter by ID or name from backup array
      this.productActiveCountries = this.allProductActiveCountries.filter(product => {
        const idText = (product.id || '').toString().toLowerCase();
        const nameText = (product.name || '').toLowerCase();

        return idText.includes(queryLower) || nameText.includes(queryLower);
      });
    }
  }

  private resetCodeFieldsState() {
    // Reset the 4 code fields (Account, Action, Coverage, Insurance)
    this.userform.get('optionAccount')?.disable({ emitEvent: false });
    this.userform.get('optionActionCode')?.disable({ emitEvent: false });
    this.userform.get('optionCoverageCode')?.disable({ emitEvent: false });
    this.userform.get('optionInsuranceCode')?.disable({ emitEvent: false });

    this.userform.get('optionAccount')?.setValue(undefined, { emitEvent: false });
    this.userform.get('optionActionCode')?.setValue(undefined, { emitEvent: false });
    this.userform.get('optionCoverageCode')?.setValue(undefined, { emitEvent: false });
    this.userform.get('optionInsuranceCode')?.setValue(undefined, { emitEvent: false });

    // Reset state variables
    this.productPlanYear = '';
    this.productAccount = '';
    this.productActionCode = '';
    this.productCoverageCode = '';
    this.productInsuranceCode = '';

    this.accountCodeUppercase = '';
    this.actionCodeUppercase = '';
    this.coverageUppercase = '';
    this.insuranceCodeUppercase = '';

    this.editableAccount = false;
    this.editableActionCodeOWS = false;
    this.editableCoverageCodeOWS = false;
    this.editableInsuranceCode = false;

    this.placeholderAccountCode = 'TRANSLATE.SELECT';
    this.placeholderActionCodeOWS = 'TRANSLATE.SELECT';
    this.placeholderCoverageCodeOWS = 'TRANSLATE.SELECT';
    this.placeholderInsuranceCode = 'TRANSLATE.SELECT';

    this.messageAccountCode = false;
    this.messageActionCodeOWS = false;
    this.messageCoverageCodeOWS = false;
    this.messageInsuranceCode = false;
  }

  private resetProductSelectionState() {
    this.productActiveCountries = [];
    this.allProductActiveCountries = [];
    this.productActive = new ProductActive();
    this.selectedProductName = null;
    this.userform.get('optionProductName')?.reset(null, { emitEvent: false });
    this.userform.get('optionProductName')?.disable({ emitEvent: false });
    this.userform.get('optionProductName')?.markAsPristine();
    this.userform.get('optionProductName')?.markAsUntouched();

    // Disable and reset the 4 code fields
    this.userform.get('optionAccount')?.disable({ emitEvent: false });
    this.userform.get('optionActionCode')?.disable({ emitEvent: false });
    this.userform.get('optionCoverageCode')?.disable({ emitEvent: false });
    this.userform.get('optionInsuranceCode')?.disable({ emitEvent: false });

    this.userform.get('optionAccount')?.setValue(undefined, { emitEvent: false });
    this.userform.get('optionActionCode')?.setValue(undefined, { emitEvent: false });
    this.userform.get('optionCoverageCode')?.setValue(undefined, { emitEvent: false });
    this.userform.get('optionInsuranceCode')?.setValue(undefined, { emitEvent: false });

    this.productPlanYear = '';
    this.productAccount = '';
    this.productActionCode = '';
    this.productCoverageCode = '';
    this.productInsuranceCode = '';

    this.accountCodeUppercase = '';
    this.actionCodeUppercase = '';
    this.coverageUppercase = '';
    this.insuranceCodeUppercase = '';

    this.editableAccount = false;
    this.editableActionCodeOWS = false;
    this.editableCoverageCodeOWS = false;
    this.editableInsuranceCode = false;

    this.messageAccountCode = false;
    this.messageActionCodeOWS = false;
    this.messageCoverageCodeOWS = false;
    this.messageInsuranceCode = false;

    this.placeholderAccountCode = 'TRANSLATE.SELECT';
    this.placeholderActionCodeOWS = 'TRANSLATE.SELECT';
    this.placeholderCoverageCodeOWS = 'TRANSLATE.SELECT';
    this.placeholderInsuranceCode = 'TRANSLATE.SELECT';

    this.listAccountCodeOWS = [
      { label: ConstantsFordProtect.ESB, value: ConstantsFordProtect.ESB },
      { label: ConstantsFordProtect.ESBG, value: ConstantsFordProtect.ESBG },
      { label: ConstantsFordProtect.ESMP, value: ConstantsFordProtect.ESMP },
      { label: ConstantsFordProtect.EXTE, value: ConstantsFordProtect.EXTE },
      { label: ConstantsFordProtect.FLEE, value: ConstantsFordProtect.FLEE },
      { label: ConstantsFordProtect.FLSN, value: ConstantsFordProtect.FLSN },
      { label: ConstantsFordProtect.FLMT, value: ConstantsFordProtect.FLMT },
      { label: ConstantsFordProtect.NULO, value: this.nullOptionValue },
      { label: ConstantsFordProtect.OTHERS, value: ConstantsFordProtect.OTHERS }
    ];

    this.listActionCodeOWS = [
      { label: ConstantsFordProtect.A, value: ConstantsFordProtect.A },
      { label: ConstantsFordProtect.NULO, value: this.nullOptionValue },
      { label: ConstantsFordProtect.OTHERS, value: ConstantsFordProtect.OTHERS }
    ];

    this.listCoverageCodeOWS = [
      { label: ConstantsFordProtect.AST, value: ConstantsFordProtect.AST },
      { label: ConstantsFordProtect.ESP, value: ConstantsFordProtect.ESP },
      { label: ConstantsFordProtect.FLMT, value: ConstantsFordProtect.FLMT },
      { label: ConstantsFordProtect.FLSN, value: ConstantsFordProtect.FLSN },
      { label: ConstantsFordProtect.QCM, value: ConstantsFordProtect.QCM },
      { label: ConstantsFordProtect.NULO, value: this.nullOptionValue },
      { label: ConstantsFordProtect.OTHERS, value: ConstantsFordProtect.OTHERS }
    ];

    this.listInsuranceCode = [
      { label: ConstantsFordProtect.AST, value: ConstantsFordProtect.AST },
      { label: ConstantsFordProtect.MNTC, value: ConstantsFordProtect.MNTC },
      { label: ConstantsFordProtect.QCM, value: ConstantsFordProtect.QCM },
      { label: ConstantsFordProtect.REP, value: ConstantsFordProtect.REP },
      { label: ConstantsFordProtect.WTY, value: ConstantsFordProtect.WTY },
      { label: ConstantsFordProtect.NULO, value: this.nullOptionValue },
      { label: ConstantsFordProtect.OTHERS, value: ConstantsFordProtect.OTHERS }
    ];
  }

  private getProductById(productId: number) {
    this.productService.getProductById(productId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {

          this.selectedProductName = productId;
          this.productPlanYear = response.planYear;
          this.productAccount = response.accountNumber;
          this.accountCodeUppercase = this.productAccount;

          this.listAccountCodeOWS = [
            { label: ConstantsFordProtect.ESB, value: ConstantsFordProtect.ESB },
            { label: ConstantsFordProtect.ESBG, value: ConstantsFordProtect.ESBG },
            { label: ConstantsFordProtect.ESMP, value: ConstantsFordProtect.ESMP },
            { label: ConstantsFordProtect.EXTE, value: ConstantsFordProtect.EXTE },
            { label: ConstantsFordProtect.FLEE, value: ConstantsFordProtect.FLEE },
            { label: ConstantsFordProtect.FLSN, value: ConstantsFordProtect.FLSN },
            { label: ConstantsFordProtect.FLMT, value: ConstantsFordProtect.FLMT },
            { label: ConstantsFordProtect.NULO, value: this.nullOptionValue },
            { label: ConstantsFordProtect.OTHERS, value: ConstantsFordProtect.OTHERS }
          ];

          this.productActionCode = response.actionCode;

          this.actionCodeUppercase = this.productActionCode;

          this.listActionCodeOWS = [
            { label: ConstantsFordProtect.A, value: ConstantsFordProtect.A },
            { label: ConstantsFordProtect.NULO, value: this.nullOptionValue },
            { label: ConstantsFordProtect.OTHERS, value: ConstantsFordProtect.OTHERS }
          ];

          this.productCoverageCode = response.coverageCode;
          this.coverageUppercase = this.productCoverageCode;

          this.listCoverageCodeOWS = [
            { label: ConstantsFordProtect.AST, value: ConstantsFordProtect.AST },
            { label: ConstantsFordProtect.ESP, value: ConstantsFordProtect.ESP },
            { label: ConstantsFordProtect.FLMT, value: ConstantsFordProtect.FLMT },
            { label: ConstantsFordProtect.FLSN, value: ConstantsFordProtect.FLSN },
            { label: ConstantsFordProtect.QCM, value: ConstantsFordProtect.QCM },
            { label: ConstantsFordProtect.NULO, value: this.nullOptionValue },
            { label: ConstantsFordProtect.OTHERS, value: ConstantsFordProtect.OTHERS }
          ];

          this.productInsuranceCode = response.insuranceCode;
          this.insuranceCodeUppercase = this.productInsuranceCode;

          this.listInsuranceCode = [
            { label: ConstantsFordProtect.AST, value: ConstantsFordProtect.AST },
            { label: ConstantsFordProtect.MNTC, value: ConstantsFordProtect.MNTC },
            { label: ConstantsFordProtect.QCM, value: ConstantsFordProtect.QCM },
            { label: ConstantsFordProtect.REP, value: ConstantsFordProtect.REP },
            { label: ConstantsFordProtect.WTY, value: ConstantsFordProtect.WTY },
            { label: ConstantsFordProtect.NULO, value: this.nullOptionValue },
            { label: ConstantsFordProtect.OTHERS, value: ConstantsFordProtect.OTHERS }
          ];
          this.coverageCategory = new CoverageCategory();

          // FIRST: Reset editable flags to false (will be set to true only for custom values)
          this.editableAccount = false;
          this.editableActionCodeOWS = false;
          this.editableCoverageCodeOWS = false;
          this.editableInsuranceCode = false;

          this.placeholderAccountCode = 'TRANSLATE.SELECT';
          this.placeholderActionCodeOWS = 'TRANSLATE.SELECT';
          this.placeholderCoverageCodeOWS = 'TRANSLATE.SELECT';
          this.placeholderInsuranceCode = 'TRANSLATE.SELECT';

          // Enable the 4 code fields and populate them with product data
          this.userform.get('optionAccount')?.enable({ emitEvent: false });
          this.userform.get('optionActionCode')?.enable({ emitEvent: false });
          this.userform.get('optionCoverageCode')?.enable({ emitEvent: false });
          this.userform.get('optionInsuranceCode')?.enable({ emitEvent: false });

          // Denormalize null values to sentinel for UI display
          const denormAccount = this.denormalizeOptionValue(this.productAccount);
          const denormAction = this.denormalizeOptionValue(this.productActionCode);
          const denormCoverage = this.denormalizeOptionValue(this.productCoverageCode);
          const denormInsurance = this.denormalizeOptionValue(this.productInsuranceCode);



          // SECOND: Detect and configure custom values BEFORE setValue to avoid onChange interference
          // Handle custom values from DB (if value not in predefined list, set to OTHERS mode)
          // For custom values, ADD them to the options list so PrimeNG can render them
          if (this.isCustomValueFromDatabase(this.productAccount, this.listAccountCodeOWS)) {
            // Add custom value to options list so PrimeNG can render it
            this.listAccountCodeOWS = [
              ...this.listAccountCodeOWS.filter(o => o.value !== ConstantsFordProtect.OTHERS),
              { label: this.productAccount.toUpperCase(), value: this.productAccount },
              { label: ConstantsFordProtect.OTHERS, value: ConstantsFordProtect.OTHERS }
            ];
            this.accountCodeUppercase = this.productAccount ?? '';
            this.editableAccount = true;
            this.placeholderAccountCode = 'TRANSLATE.INFORM.THE.OTHER';
          }

          if (this.isCustomValueFromDatabase(this.productActionCode, this.listActionCodeOWS)) {
            // Add custom value to options list so PrimeNG can render it
            this.listActionCodeOWS = [
              ...this.listActionCodeOWS.filter(o => o.value !== ConstantsFordProtect.OTHERS),
              { label: this.productActionCode.toUpperCase(), value: this.productActionCode },
              { label: ConstantsFordProtect.OTHERS, value: ConstantsFordProtect.OTHERS }
            ];
            this.actionCodeUppercase = this.productActionCode ?? '';
            this.editableActionCodeOWS = true;
            this.placeholderActionCodeOWS = 'TRANSLATE.INFORM.THE.OTHER';
          }

          if (this.isCustomValueFromDatabase(this.productCoverageCode, this.listCoverageCodeOWS)) {
            // Add custom value to options list so PrimeNG can render it
            this.listCoverageCodeOWS = [
              ...this.listCoverageCodeOWS.filter(o => o.value !== ConstantsFordProtect.OTHERS),
              { label: this.productCoverageCode.toUpperCase(), value: this.productCoverageCode },
              { label: ConstantsFordProtect.OTHERS, value: ConstantsFordProtect.OTHERS }
            ];
            this.coverageUppercase = this.productCoverageCode ?? '';
            this.editableCoverageCodeOWS = true;
            this.placeholderCoverageCodeOWS = 'TRANSLATE.INFORM.THE.OTHER';

          }

          if (this.isCustomValueFromDatabase(this.productInsuranceCode, this.listInsuranceCode)) {
            // Add custom value to options list so PrimeNG can render it
            this.listInsuranceCode = [
              ...this.listInsuranceCode.filter(o => o.value !== ConstantsFordProtect.OTHERS),
              { label: this.productInsuranceCode.toUpperCase(), value: this.productInsuranceCode },
              { label: ConstantsFordProtect.OTHERS, value: ConstantsFordProtect.OTHERS }
            ];
            this.insuranceCodeUppercase = this.productInsuranceCode ?? '';
            this.editableInsuranceCode = true;
            this.placeholderInsuranceCode = 'TRANSLATE.INFORM.THE.OTHER';
          }

          // THIRD: Now set the values in the form
          this.userform.get('optionAccount')?.setValue(denormAccount, { emitEvent: false });
          this.userform.get('optionActionCode')?.setValue(denormAction, { emitEvent: false });
          this.userform.get('optionCoverageCode')?.setValue(denormCoverage, { emitEvent: false });
          this.userform.get('optionInsuranceCode')?.setValue(denormInsurance, { emitEvent: false });

          // FOURTH: Trigger onChange handlers manually to sync internal state
          // This ensures editableXXX flags and placeholders are set correctly
          this.accountOnChange({ value: denormAccount });
          this.actionCodeOnChange({ value: denormAction });
          this.coverageCodeOnChange({ value: denormCoverage });
          this.insuranceCodeOnChange({ value: denormInsurance });

        },
        error: (err) => {

          if (err.status === 402) {
            this.messageService.add({ severity: 'warn', summary: 'Atenção', detail: 'Pagamento recusado. Verifique os dados do cartão e tente novamente.' })
            return;
          }
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('TRANSLATE.ERROR'),
            detail: this.translateService.instant('TRANSLATE.PRODUCTS.NOT.FOUND')
          });
        }
      });
  }

  accountOnChange(event: ValueChangeEvent<string | null>) {

    this.messageAccountCode = false;

    if (event.value === ConstantsFordProtect.OTHERS) {
      // User explicitly selects OUTROS
      this.editableAccount = true;
      this.placeholderAccountCode = 'TRANSLATE.INFORM.THE.OTHER';
      this.accountCodeUppercase = '';
      this.userform.get('optionAccount')?.setValue('', { emitEvent: false });
    } else if (this.editableAccount && !this.isOptionValue(this.listAccountCodeOWS, event.value)) {
      // Editable mode active and value is custom (not in list) - keep editing
      this.accountCodeUppercase = this.normalizeEditableValue(event.value, 9);
      this.userform.get('optionAccount')?.setValue(this.accountCodeUppercase, { emitEvent: false });
    } else if (this.isOptionValue(this.listAccountCodeOWS, event.value)) {
      // Value is a predefined option (including NULL sentinel)
      this.editableAccount = false;
      this.placeholderAccountCode = 'TRANSLATE.SELECT';
      this.accountCodeUppercase = event.value ?? '';
    } else {
      // Unknown state - disable editable
      this.editableAccount = false;
      this.placeholderAccountCode = 'TRANSLATE.SELECT';
      this.accountCodeUppercase = event.value ?? '';
    }

    this.userform.get('optionAccount')?.updateValueAndValidity({ emitEvent: false });

  }

  actionCodeOnChange(event: ValueChangeEvent<string | null>) {

    this.messageActionCodeOWS = false;

    if (event.value === ConstantsFordProtect.OTHERS) {
      // User explicitly selects OUTROS
      this.editableActionCodeOWS = true;
      this.placeholderActionCodeOWS = 'TRANSLATE.INFORM.THE.OTHER';
      this.actionCodeUppercase = '';
      this.userform.get('optionActionCode')?.setValue('', { emitEvent: false });
    } else if (this.editableActionCodeOWS && !this.isOptionValue(this.listActionCodeOWS, event.value)) {
      // Editable mode active and value is custom (not in list) - keep editing
      this.actionCodeUppercase = this.normalizeEditableValue(event.value, 1);
      this.userform.get('optionActionCode')?.setValue(this.actionCodeUppercase, { emitEvent: false });
    } else if (this.isOptionValue(this.listActionCodeOWS, event.value)) {
      // Value is a predefined option (including NULL sentinel)
      this.editableActionCodeOWS = false;
      this.placeholderActionCodeOWS = 'TRANSLATE.SELECT';
      this.actionCodeUppercase = event.value ?? '';
    } else {
      // Unknown state - disable editable
      this.editableActionCodeOWS = false;
      this.placeholderActionCodeOWS = 'TRANSLATE.SELECT';
      this.actionCodeUppercase = event.value ?? '';
    }

    this.userform.get('optionActionCode')?.updateValueAndValidity({ emitEvent: false });

  }

  coverageCodeOnChange(event: ValueChangeEvent<string | null>) {

    this.messageCoverageCodeOWS = false;

    if (event.value === ConstantsFordProtect.OTHERS) {
      // User explicitly selects OUTROS
      this.editableCoverageCodeOWS = true;
      this.placeholderCoverageCodeOWS = 'TRANSLATE.INFORM.THE.OTHER';
      this.coverageUppercase = '';
      this.userform.get('optionCoverageCode')?.setValue('', { emitEvent: false });
    } else if (this.editableCoverageCodeOWS && !this.isOptionValue(this.listCoverageCodeOWS, event.value)) {
      // Editable mode active and value is custom (not in list) - keep editing
      this.coverageUppercase = this.normalizeEditableValue(event.value, 4);
      this.userform.get('optionCoverageCode')?.setValue(this.coverageUppercase, { emitEvent: false });
    } else if (this.isOptionValue(this.listCoverageCodeOWS, event.value)) {
      // Value is a predefined option (including NULL sentinel)
      this.editableCoverageCodeOWS = false;
      this.placeholderCoverageCodeOWS = 'TRANSLATE.SELECT';
      this.coverageUppercase = event.value ?? '';
    } else {
      // Unknown state - disable editable
      this.editableCoverageCodeOWS = false;
      this.placeholderCoverageCodeOWS = 'TRANSLATE.SELECT';
      this.coverageUppercase = event.value ?? '';
    }

    this.userform.get('optionCoverageCode')?.updateValueAndValidity({ emitEvent: false });

  }

  insuranceCodeOnChange(event: ValueChangeEvent<string | null>) {

    this.messageInsuranceCode = false;

    if (event.value === ConstantsFordProtect.OTHERS) {
      // User explicitly selects OUTROS
      this.editableInsuranceCode = true;
      this.placeholderInsuranceCode = 'TRANSLATE.INFORM.THE.OTHER';
      this.insuranceCodeUppercase = '';
      this.userform.get('optionInsuranceCode')?.setValue('', { emitEvent: false });
    } else if (this.editableInsuranceCode && !this.isOptionValue(this.listInsuranceCode, event.value)) {
      // Editable mode active and value is custom (not in list) - keep editing
      this.insuranceCodeUppercase = this.normalizeEditableValue(event.value, 4);
      this.userform.get('optionInsuranceCode')?.setValue(this.insuranceCodeUppercase, { emitEvent: false });
    } else if (this.isOptionValue(this.listInsuranceCode, event.value)) {
      // Value is a predefined option (including NULL sentinel)
      this.editableInsuranceCode = false;
      this.placeholderInsuranceCode = 'TRANSLATE.SELECT';
      this.insuranceCodeUppercase = event.value ?? '';
    } else {
      // Unknown state - disable editable
      this.editableInsuranceCode = false;
      this.placeholderInsuranceCode = 'TRANSLATE.SELECT';
      this.insuranceCodeUppercase = event.value ?? '';
    }

    this.userform.get('optionInsuranceCode')?.updateValueAndValidity({ emitEvent: false });

  }

  optionCoverageTypeChange(event: ValueChangeEvent<string | null>) {
    this.selectedCoverageType = event.value;
  }

  private isOptionValue(options: EditableOption[], value: string | null | undefined): boolean {
    return options.some(option => option.value === value);
  }

  private normalizeEditableValue(value: string | null | undefined, maxLength: number): string {
    return (value || '').toString().toUpperCase().trim().slice(0, maxLength);
  }

  private insuranceCodeRequiredValidator(
    maxLength: number,
    isEditableMode: () => boolean,
    getOptions: () => EditableOption[]
  ): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;

      // Allow only the NULO marker (it is converted to null for persistence).
      if (value === this.nullOptionValue) {
        return null;
      }

      // Undefined/null/empty are invalid for the form field.
      if (value === undefined || value === null || value === '') {
        return { required: true };
      }

      const normalizedValue = value.toString().toUpperCase().trim();

      if (normalizedValue === '') {
        return { required: true };
      }

      const isPredefinedOption = this.isOptionValue(getOptions(), normalizedValue);

      // When OTHERS is selected, allow free custom text without max-length blocking.
      if (isEditableMode() && !isPredefinedOption) {
        if (normalizedValue === '') {
          return { required: true };
        }

        return normalizedValue.length <= maxLength
          ? null
          : { maxlength: { requiredLength: maxLength, actualLength: normalizedValue.length } };
      }

      return normalizedValue.length <= maxLength
        ? null
        : { maxlength: { requiredLength: maxLength, actualLength: normalizedValue.length } };
    };
  }

  private optionalCodeMaxLengthValidator(
    maxLength: number,
    isEditableMode: () => boolean,
    getOptions: () => EditableOption[]
  ): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;

      // Optional fields: empty, NULO marker and null are valid.
      if (value === undefined || value === null || value === '' || value === this.nullOptionValue) {
        return null;
      }

      const normalizedValue = value.toString().toUpperCase().trim();

      // After normalization, if empty, it's still valid (optional field).
      if (normalizedValue === '') {
        return null;
      }

      const isPredefinedOption = this.isOptionValue(getOptions(), normalizedValue);

      // When OTHERS is selected, allow free custom text without max-length blocking.
      if (isEditableMode() && !isPredefinedOption) {
        return normalizedValue.length <= maxLength
          ? null
          : { maxlength: { requiredLength: maxLength, actualLength: normalizedValue.length } };
      }

      return normalizedValue.length <= maxLength
        ? null
        : { maxlength: { requiredLength: maxLength, actualLength: normalizedValue.length } };
    };
  }

  private normalizeOptionValue(value: string | null | undefined): string | null {
    if (value === this.nullOptionValue || value === undefined || value === null) {
      return null;
    }

    const normalizedValue = value.toString().toUpperCase().trim();
    return normalizedValue === '' ? null : normalizedValue;
  }

  private denormalizeOptionValue(value: string | null | undefined): string | null {
    // Convert null from database to sentinel value for UI display
    if (value === null || value === undefined) {
      return this.nullOptionValue;
    }
    return value;
  }

  private isCustomValueFromDatabase(
    value: string | null | undefined,
    options: EditableOption[]
  ): boolean {
    // A custom value is one that is not null, not the sentinel, and not in the predefined options list
    if (value === null || value === undefined || value === this.nullOptionValue) {
      return false;
    }
    return !this.isOptionValue(options, value);
  }

  private handleCustomValueFromDatabase(
    formControlName: string,
    value: string | null | undefined,
    options: EditableOption[],
    editableFlag: (state: boolean) => void,
    placeholderGetter: (placeholder: string) => void
  ): void {
    // If the value from DB is custom (not in predefined options), set to OTHERS mode
    if (this.isCustomValueFromDatabase(value, options)) {
      this.userform.get(formControlName)?.setValue(ConstantsFordProtect.OTHERS, { emitEvent: false });
      editableFlag(true);
      placeholderGetter('TRANSLATE.INFORM.THE.OTHER');
      // Keep the custom value in the uppercase tracking variable for display
    }
  }

  buttonSave() {
    this.submitted = true;

    const formValues = this.userform.value;
    this.selectedCountry = formValues.optionCountry;
    this.selectedCoverageType = formValues.optionCoverageType;
    const normalizedAccountCode = this.normalizeOptionValue(formValues.optionAccount);
    const normalizedActionCode = this.normalizeOptionValue(formValues.optionActionCode);
    const normalizedCoverageCode = this.normalizeOptionValue(formValues.optionCoverageCode);
    const normalizedInsuranceCode = this.normalizeOptionValue(formValues.optionInsuranceCode);

    this.accountCodeUppercase = normalizedAccountCode ?? '';
    this.actionCodeUppercase = normalizedActionCode ?? '';
    this.coverageUppercase = normalizedCoverageCode ?? '';
    this.insuranceCodeUppercase = normalizedInsuranceCode ?? '';

    if (this.selectedCountry === undefined || this.selectedCountry === null) {
      this.userform.get('optionCountry')?.markAsTouched();
      this.selectedCountry = null;
    }
    if (this.userform.get('optionProductName')?.enabled && (this.selectedProductName === undefined || this.selectedProductName === null)) {
      this.userform.get('optionProductName')?.markAsTouched();
      this.selectedProductName = null;
    }
    if (this.selectedCoverageType === undefined || this.selectedCoverageType === null) {
      this.userform.get('optionCoverageType')?.markAsTouched();
      this.selectedCoverageType = null;
    }
    if (this.userform.get('inputCoverageName')?.invalid) {
      this.userform.get('inputCoverageName')?.markAsPending();
    }
    if (this.userform.get('inputCoverageDescription')?.invalid) {
      this.userform.get('inputCoverageDescription')?.markAsPending();
    }
    if (this.userform.get('inputOwsCoverage')?.invalid) {
      this.userform.get('inputOwsCoverage')?.markAsPending();
    }
    if (this.userform.get('inputMonths')?.invalid) {
      this.userform.get('inputMonths')?.markAsPending();
    }
    if (this.userform.get('inputDistance')?.invalid) {
      this.userform.get('inputDistance')?.markAsPending();
    }
    if (this.userform.get('inputTotalVisits')?.invalid) {
      this.userform.get('inputTotalVisits')?.markAsPending();
    }
    if (this.userform.get('inputInitialVisit')?.invalid) {
      this.userform.get('inputInitialVisit')?.markAsPending();
    }
    if (this.userform.get('inputDistance')?.invalid) {
      this.userform.get('inputDistance')?.markAsPending();
    }
    if (this.userform.get('inputTotalVisits')?.invalid) {
      this.userform.get('inputTotalVisits')?.markAsPending();
    }

    // Reset all error messages (these only show when user selects OUTROS without providing custom value)
    this.messageAccountCode = false;
    this.messageActionCodeOWS = false;
    this.messageCoverageCodeOWS = false;
    this.messageInsuranceCode = false;

    // Check if any field is in OUTROS mode but empty (only this requires error message)
    const hasAccountError = this.accountCodeUppercase === ConstantsFordProtect.OTHERS && this.editableAccount && !this.accountCodeUppercase;
    const hasActionError = this.actionCodeUppercase === ConstantsFordProtect.OTHERS && this.editableActionCodeOWS && !this.actionCodeUppercase;
    const hasCoverageError = this.coverageUppercase === ConstantsFordProtect.OTHERS && this.editableCoverageCodeOWS && !this.coverageUppercase;
    const hasInsuranceError = this.insuranceCodeUppercase === ConstantsFordProtect.OTHERS && this.editableInsuranceCode && !this.insuranceCodeUppercase;

    if (hasAccountError) {
      this.messageAccountCode = true;
    }
    if (hasActionError) {
      this.messageActionCodeOWS = true;
    }
    if (hasCoverageError) {
      this.messageCoverageCodeOWS = true;
    }
    if (hasInsuranceError) {
      this.messageInsuranceCode = true;
    }

    if (hasAccountError) {
      this.focusField('optionAccount');
    } else if (hasActionError) {
      this.focusField('optionActionCode');
    } else if (hasCoverageError) {
      this.focusField('optionCoverageCode');
    } else if (hasInsuranceError) {
      this.focusField('optionInsuranceCode');
    }

    // If any error or form invalid, stop here
    if (hasAccountError || hasActionError || hasCoverageError || hasInsuranceError || !this.userform.valid) {
      if (!hasAccountError && !hasActionError && !hasCoverageError && !hasInsuranceError) {
        this.focusFirstInvalidField();
      }
      if (!this.userform.valid && !this.hasCoverageMinLengthError()) {
        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant('TRANSLATE.ERROR'),
          detail: this.translateService.instant('TRANSLATE.ERROR.PLEASE.REQUIRED.FIELDS')
        });
      }
      return;
    }

    const selectedProductId = this.selectedProductName;
    if (this.userform.get('optionProductName')?.enabled && selectedProductId === null) {
      this.userform.get('optionProductName')?.markAsTouched();
      this.focusField('optionProductName');
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('TRANSLATE.ERROR'),
        detail: this.translateService.instant('TRANSLATE.ERROR.PLEASE.REQUIRED.FIELDS')
      });
      return;
    }

    const owsCoverageValue = (formValues.inputOwsCoverage || '').toString().trim();
    const distanceInputValue = (formValues.inputDistance || '').toString();

    this.newCoverageCategory.productId = selectedProductId as number;
    this.newCoverageCategory.type = this.selectedCoverageType!;
    this.newCoverageCategory.name = (formValues.inputCoverageName || '').toString().trim();
    this.newCoverageCategory.description = (formValues.inputCoverageDescription || '').toString().trim();
    this.newCoverageCategory.owsCoverage = owsCoverageValue.toUpperCase();
    this.newCoverageCategory.months = (formValues.inputMonths || '').toString();
    this.newCoverageCategory.visit = Number(formValues.inputTotalVisits);
    this.newCoverageCategory.startVisit = Number(formValues.inputInitialVisit);
    this.newCoverageCategory.distanceUnit = ConstantsFordProtect.DISTANCE_UNIT;
    this.newCoverageCategory.termUnit = ConstantsFordProtect.TERM_UNIT;
    this.newCoverageCategory.createUser = this.apsPermissionService.getUserCsid();
    this.newCoverageCategory.distance = this.inputOdometer ?? Number(distanceInputValue.replace(/\./g, ''));

    this.newCoverageCategory.accountNumber = normalizedAccountCode;
    this.newCoverageCategory.actionCode = normalizedActionCode;
    this.newCoverageCategory.coverageCode = normalizedCoverageCode;
    this.newCoverageCategory.insuranceCode = normalizedInsuranceCode;

    this.isSaving = true;

    this.coverageService.createCoverage(this.newCoverageCategory)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: NewCoverageCategory) => {

          this.newCoverageCategory = response;
          this.isSaving = false;
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('TRANSLATE.SUCCESS'),
            detail: this.translateService.instant('TRANSLATE.SUCCESS.CREATE')
          });
          this.buttonReturn();
        },
        error: (err) => {
          this.isSaving = false;
          if (err.status === 402) {
            this.messageService.add({ severity: 'warn', summary: 'Atenção', detail: 'Pagamento recusado. Verifique os dados do cartão e tente novamente.' })
            return;
          }
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('TRANSLATE.ERROR'),
            detail: this.translateService.instant('TRANSLATE.ERROR.CREATE.COVERAGE')
          });
        }
      });

  }

  hasControlRequiredError(controlName: string): boolean {
    return !!this.submitted && !!this.userform?.get(controlName)?.hasError('required');
  }

  hasControlGreaterThanZeroError(controlName: string): boolean {
    return !!this.submitted && !!this.userform?.get(controlName)?.hasError('min');
  }

  hasControlMinLengthError(controlName: string): boolean {
    return !!this.submitted && !!this.userform?.get(controlName)?.hasError('minlength');
  }

  private hasCoverageMinLengthError(): boolean {
    return this.hasControlMinLengthError('inputCoverageName')
      || this.hasControlMinLengthError('inputCoverageDescription')
      || this.hasControlMinLengthError('inputOwsCoverage');
  }

  private focusFirstInvalidField(): void {
    for (const controlName of this.focusOrder) {
      const control = this.userform.get(controlName);
      if (!control || control.disabled) {
        continue;
      }

      if (control.invalid || control.pending) {
        this.focusField(controlName);
        return;
      }
    }
  }

  private focusField(controlName: string): void {
    const selectorByControl: Record<string, string> = {
      optionCountry: '#country',
      optionProductName: '#productId',
      optionCoverageType: '#type',
      inputCoverageName: '#coverageName',
      inputCoverageDescription: '#coverageDescription',
      inputOwsCoverage: '#owsCoverage',
      inputMonths: '#months',
      inputDistance: '#distance',
      inputTotalVisits: '#visits',
      inputInitialVisit: '#startVisit',
      optionAccount: '#account',
      optionActionCode: '#actionCode',
      optionCoverageCode: '#coverageCode',
      optionInsuranceCode: '#insuranceCode'
    };

    const selector = selectorByControl[controlName];
    if (!selector) {
      return;
    }

    setTimeout(() => {
      const host = document.querySelector(selector) as HTMLElement | null;
      if (!host) {
        return;
      }

      const focusableChild = host.querySelector('input, textarea, .p-dropdown-label, .p-multiselect-label, [tabindex]') as HTMLElement | null;
      const target = focusableChild ?? host;

      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      target.focus();
    }, 0);
  }

}